import { Formik } from 'formik';
import { useState, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import { nationalParkSchema } from '../../../../../constants';
import { DAV_APIS } from '../../../../../adapters';
import { Alert } from '../../../../commons';
import NationalParkForm from '../NationalParkForm';
import { useSearchQuery } from '../../../../../hooks';
import DashboardLoader from '../../../sections/Loader/DashboardLoader';
import { Button } from '../../../../ui/Button';

const normalizeBlocks = (raw: any): any[] => {
  if (!raw) return [];
  if (typeof raw === 'string') { try { return normalizeBlocks(JSON.parse(raw)); } catch { return []; } }
  if (Array.isArray(raw)) return raw.flatMap((item: any) => typeof item === 'string' ? normalizeBlocks(item) : [item]);
  return [];
};

const EditNationalParkPage: FC = () => {
  const [loading, setLoading] = useState(false);
  const [park, setPark] = useState<any>({});
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();

  const parkId = useSearchQuery('park') as string;

  useEffect(() => {
    const fetchPark = async () => {
      if (!parkId) return;
      setIsFetching(true);
      try {
        const res = await DAV_APIS.nationalPark.get.getNationalParkById(parkId);
        if (res.status === 200) {
          setPark(res.data.nationalPark);
        }
      } catch (error) {
        Alert({ variant: 'error', message: 'Failed to load national park data' });
      }
      setIsFetching(false);
    };
    fetchPark();
    window.scrollTo(0, 0);
  }, [parkId]);

  const submitHandler = async (values: any) => {
    setLoading(true);
    try {
      const data = {
        name: values.name,
        descriptionTitle: values.descriptionTitle,
        description: values.description,
        location: values.location,
        bestTimeToVisit: values.bestTimeToVisit,
        country: values.country,
        tours: values.tours,
        file: values.file,
        blocks: values.blocks,
      };
      const res = await DAV_APIS.nationalPark.editNationalPark(data, parkId);
      setLoading(false);
      if (res.status === 200) {
        Alert({ variant: 'success', message: 'National park updated successfully' });
        navigate('/dashboard/national-parks');
      }
    } catch (error) {
      setLoading(false);
      Alert({ variant: 'error', message: 'Failed to update national park, please try again' });
    }
  };

  return (
    <div className='w-full space-y-6 pt-4 pb-10'>
      <div className='flex items-center'>
        <button
          onClick={() => navigate('/dashboard/national-parks')}
          type='button'
          className='flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors font-semibold'
        >
          <MdArrowBackIosNew className='w-4 h-4' />
          <span>Back to National Parks</span>
        </button>
      </div>

      <div className='bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm min-h-[400px]'>
        <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white mb-8 text-center tracking-tight'>
          Edit National Park
        </h1>

        {isFetching ? (
          <div className='py-20 flex justify-center'>
            <DashboardLoader />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              name: park.name || '',
              descriptionTitle: park.descriptionTitle || '',
              description: park.description || '',
              location: park.location || '',
              bestTimeToVisit: park.bestTimeToVisit || '',
              country: park.country?.id || park.country || '',
              tours: park.tours || '',
              file: '',
              imageCover: park.image || '',
              blocks: normalizeBlocks(park.blocks),
              blockTitle: '',
              blockDescription: '',
            }}
            validationSchema={nationalParkSchema}
            onSubmit={async (values, { resetForm }) => {
              await submitHandler(values);
              resetForm();
            }}
          >
            {({ handleSubmit, values, setFieldValue, dirty, isValid }) => (
              <form onSubmit={handleSubmit} className='w-full'>
                <NationalParkForm values={values} setFieldValue={setFieldValue} />
                <div className='flex justify-center mt-10 border-t border-gray-100 dark:border-gray-700 pt-8'>
                  <Button
                    type='submit'
                    variant='primary'
                    loading={loading}
                    disabled={!dirty || !isValid}
                    className='w-full sm:w-64'
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default EditNationalParkPage;
